import React, { useEffect, useRef, useState } from 'react';
import { Grid, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Box, Backdrop, CircularProgress, Skeleton, Select, MenuItem, Stack, useTheme, useMediaQuery, Table, TableCell, TableRow, TableHead, IconButton, Checkbox, FormControlLabel } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import { useNavigate, useParams } from 'react-router-dom';
import { ShoppingCartClient } from '../../../api/ShoppingCart/ShoppingCartClient';
import { AVANGARD_WEB_URL, REACT_APP_APPLICATION_ISPROD, REACT_APP_PORTAL_API_URL } from '../../../KeyVariables';
import { PaymentTypeDTO, ShoppingCartDTO } from '../../../api/ShoppingCart/ShoppingCartClientDTO';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ApiException, BaseNomDTO } from '../../../api/ClientDTO';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { TransactionClient } from '../../../api/onlineTransaction/onlineTransactionClient';
import StyledQR from './miaQrCode';
import { useSignalR } from '../../../contexts/SignalRContext';
import ErrorIcon from '@mui/icons-material/Error';
import { format } from 'date-fns';
import ConfirmationCommonDialog from '../../Dialog/ConfirmationCommonDialog';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import SamplingScheduleDialog from './SamplingScheduleDialog';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
function ShoppingCart() {
  const [items, setItems] = useState([]);
  const [shoppingCartObject, setShoppingCartObject] = useState<ShoppingCartDTO | undefined>();
  const [latestPendingShoppingCartObject, setLatestPendingShoppingCartObject] = useState<ShoppingCartDTO | undefined>();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [reimbursmentDialogOpen, setReimbursmentDialogOpen] = useState(false);
  const [pendingErrorOpen, setPendingErrorOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [discounts, setDiscounts] = useState({});
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [canBeReimbursed, setCanBeReimbursed] = useState(false);
  const [paymentRef, setPaymentRef] = useState("");
  const shoppingCartClient = new ShoppingCartClient(REACT_APP_PORTAL_API_URL);
  const { shoppingCartIdentity } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [selectedPaymentTypeId, setSelectedPaymentTypeId] = useState<number | null>(null);

  const paymentTypeImages: { [key: number]: string } = {
    2: '/static/images/payments/mia.png',
    3: '/static/images/payments/card.png',
  };
  const paymentWindowRef = useRef<Window | null>(null);

  const handleCheckout = () => {
    setOpenDialog(true);
  };

  const { connectionState, addEventListener, removeEventListener } = useSignalR();

  useEffect(() => {
    const handler = (shoppingCartIdentity: string) => {
      if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
        paymentWindowRef.current.close();
        paymentWindowRef.current = null;
      }

      window.open('/shoppingcart/' + shoppingCartIdentity, '_self');
    };

    addEventListener('PaymentConfirmation', handler);

    return () => {
      removeEventListener('PaymentConfirmation', handler);
    };
  }, [addEventListener, removeEventListener]);


  const deleteItem = (item: any) => {
    setItems(items.filter(x => x.id != item.id));
    shoppingCartClient.deleteshoppingcartitem({ id: item.id } as any)
  }

  const canEdit = shoppingCartObject?.shoppingCartStatus?.id == 1 && !loadingPayment && !paymentSuccess;

  useEffect(() => {
    const fetch = async () => {
      const response = await shoppingCartClient.paymenttypes();
      setPaymentTypes(response);
    }
    fetch();
  }, [])
  const [paymentTypes, setPaymentTypes] = useState<PaymentTypeDTO[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    fetchAsync();
  }, [shoppingCartIdentity]);

  function parseJSON(value) {
    try {
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return value;
    }
  }

  useEffect(() => {
    setAgreeToTerms(shoppingCartObject?.shoppingCartStatus?.id != 1)
    setSelectedPaymentTypeId(shoppingCartObject?.shoppingCartPaymentType?.id || null)
  }, [shoppingCartObject?.shoppingCartStatus, shoppingCartObject?.shoppingCartPaymentType?.id])

  const fetchAsync = async () => {
    try {
      setLoading(true);
      if (shoppingCartIdentity) {
        const response = await shoppingCartClient.byidentity(shoppingCartIdentity);

        if (response) {
          const latestPending = await shoppingCartClient.getlatestpendingcart();
          if (latestPending && shoppingCartIdentity != latestPending.shoppingCartIdentity) {
            setLatestPendingShoppingCartObject(latestPending)
            setShoppingCartObject(response);
            setPendingErrorOpen(true)
            return;
          }

          if (!response.patient || !response.patient.id || response.patient.id == 0) {
            const res = await shoppingCartClient.addpatient(response);
            if (res) {
              setShoppingCartObject(res);
            }
          }
          else {
            setShoppingCartObject(response)
          }
          if (response.shoppingCartStatus?.id != 3) {
            setLoadingPayment(false)
          }
          if (response.onlineTransaction?.paymentReference) {
            setPaymentRef(response.onlineTransaction?.paymentReference)
          }
          if(response.shoppingCartPaymentType?.id! > 1 && (
            response.shoppingCartStatus?.id == 5 ||
            response.shoppingCartStatus?.id == 6
          )){
            try{
              setCanBeReimbursed(await new TransactionClient(REACT_APP_PORTAL_API_URL).checkreimbursmentavailabilty(shoppingCartIdentity))
            }catch(e){
              console.error(e)
            }
          }
        }
        if (shoppingCartIdentity) {
          const responseItems = await shoppingCartClient.shoppingcartitemsbyidentity(shoppingCartIdentity);
          if (responseItems && responseItems.length > 0) {
            setItems(
              responseItems.map((s) => ({
                ...s,
                additionalParameters: parseJSON(s.additionalParameters),
              } as any))
            );
          }
        }
      }
    } catch (ex) {
      console.error(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscountChange = (id, discount) => {
    setDiscounts((prev) => ({ ...prev, [id]: discount }));
  };

  const calculateDiscountedPrice = (price, discount) => {
    return price - (price * discount) / 100;
  };
  const totalPrice = items.reduce((sum, item) => {
    const discount = discounts[item.id] || 0;
    return sum + calculateDiscountedPrice(item.service?.price || 0, discount);
  }, 0);

  const handlePayment = async () => {
    setLoadingPayment(true);
    if (selectedPaymentTypeId == 1) {
      try {
        const response = await shoppingCartClient.pay(shoppingCartIdentity);

        if (response) {
          setConfirmed(true)
          setPaymentSuccess(true);
        }
      } catch (ex) {
        console.error(ex);
      } finally {
        setLoadingPayment(false);
      }
    } else {
      console.log(paymentTypes)
      try {
        const paymentRedirectUrl = await new TransactionClient(REACT_APP_PORTAL_API_URL).initiate(paymentTypes.filter(x => x.id == selectedPaymentTypeId)[0]!.tag!, shoppingCartIdentity!)
        setPaymentRef(paymentRedirectUrl)
        if (selectedPaymentTypeId == 2) {
          paymentWindowRef.current = window.open(
            paymentRedirectUrl,
            'PaymentWindow',
            'width=800,height=600,resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no'
          );
        } else {
          window.open(paymentRedirectUrl, '_self')
        }
      }
      catch (err: any) {
        console.log(err)
        if (err.message === "notnew") {
          window.location.reload();
        } else {
          console.error(err);
        }

        setLoadingPayment(false);
      }

    }
  }
  const handleOpenCalendar = (item: any) => {


    const searchParams = new URLSearchParams();

    if (item?.service?.id) searchParams.append("serviceId", item?.service?.id);
    if (item.additionalParameters?.ConsultationId) searchParams.append("consultationId", item.additionalParameters?.ConsultationId);
    if (item.additionalParameters?.PresentationInvestigationTypeId) searchParams.append("investigationId", item.additionalParameters?.PresentationInvestigationTypeId);
    searchParams.append("shoppingCartItemId", item.id);

    navigate(`/calendar?${searchParams.toString()}`);
  };

  const handleOpenScheduleAnalysis = (item: any) => {

  }

  const [isOpenSchedule, setIsOpenSchedule] = useState({ medicalAnalysisBatchId: null, state: false })
  const reloadData = async () => {
    try {
      setLoading(true);
      setIsOpenSchedule({ medicalAnalysisBatchId: null, state: false });
      if (shoppingCartIdentity) {
        const responseItems = await shoppingCartClient.shoppingcartitemsbyidentity(shoppingCartIdentity);
        if (responseItems && responseItems.length > 0) {
          setItems(
            responseItems.map((s) => ({
              ...s,
              additionalParameters: parseJSON(s.additionalParameters),
            } as any))
          );
        }
      }
    }
    catch (ex) {
      console.error("Error in load data")
    }
    finally {
      setLoading(false);
    }
  }
  const handleClickScheduleSampling = ( medicalAnalysisBatchId ) => {
    console.log(medicalAnalysisBatchId);
    setIsOpenSchedule({ state: true, medicalAnalysisBatchId: medicalAnalysisBatchId })
  }
  const closeSchedule = () => {
    setIsOpenSchedule({ state: false, medicalAnalysisBatchId: null })
  }
  const { t } = useTranslation("Portal");

  const cancelPayment = async () => {
    await shoppingCartClient.cancel(shoppingCartIdentity);
    window.location.reload();
  }

  const cancelPendingPayment = async () => {
    await shoppingCartClient.cancel(latestPendingShoppingCartObject?.shoppingCartIdentity!);
    window.open('/shoppingcart/' + shoppingCartIdentity, '_self')
  }

  const reimbursePayment = async () => {
    setLoading(true)
    try {
      await new TransactionClient(REACT_APP_PORTAL_API_URL).reimbursment(shoppingCartIdentity);
    }
    catch(e){
      console.error(e)
    }

    window.location.reload();
  }

  const simulatePayment = async () => {
    await new TransactionClient(REACT_APP_PORTAL_API_URL).simulate(paymentTypes.filter(x => x.id == selectedPaymentTypeId)[0]!.tag!, shoppingCartIdentity!)
  }
  const hasMedicalAnalysisBatch = items?.some(s => (s as any).additionalParameters?.Type === "MedicalAnalysisBatch");
  return (
    <>
      <Helmet>
        <title>Cosul meu</title>
      </Helmet>
      {/* <StyledQR url="https://mia-qr-test.bnm.md/1/m/BNM/AGR47c1f670a67e46a59d1f964806215112" /> */}
      {loading && (
        <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Grid container spacing={3} sx={{ padding: 3 }}>
        <Grid item xs={12} md={7}>
          <Card key={'pricetable'} sx={{ marginBottom: 1, boxShadow: 10, padding: 0 }}>
            <CardContent>
              <Table>
                <TableHead>
                  <TableCell>{t('Portal_Service')}</TableCell>
                  {(shoppingCartObject?.shoppingCartStatus?.id == 5) && <TableCell sx={{ textAlign: 'center' }}>{t('Portal_Schedule')}</TableCell>}
                  <TableCell sx={{ textAlign: 'right' }}>{t('Portal_Price')} {`(lei)`}</TableCell>
                  {shoppingCartObject?.shoppingCartStatus?.id == 1 && <TableCell sx={{ textAlign: 'center', p: 0 }}></TableCell>}
                </TableHead>
                {items.map((item: any) => {
                  const discount = discounts[item.id] || 0;
                  const discountedPrice = calculateDiscountedPrice(item.service?.price || 0, discount);
                  return (
                    <TableRow>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                          {item.service?.image && (
                            <img
                              src={item.service.image}
                              alt={item.service.name || ''}
                              style={{ width: 80, height: 80, borderRadius: 1, marginRight: 5, }}
                            />
                          )}
                          <Box sx={{ flexGrow: 1 }}>
                            {item.service?.category && item.service?.category.name && <Typography variant={'caption'}>{item.service?.category?.name}</Typography>}
                            <Typography >{item.service?.name}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {
                        <TableCell sx={{ textAlign: 'center' }}>
                          {item.shoppingCartItemStatus?.id === 1 ||item.shoppingCartItemStatus?.id === 3  ? (
                            <Button
                              variant="outlined"
                              color="success"
                              startIcon={<CalendarMonthIcon />}
                            >
                              {t('Portal_Scheduled')}
                            </Button>
                          ) : (item.additionalParameters?.Type === 'MedicalAnalysisBatch' && item?.additionalParameters?.MedicalAnalysisBatchId) ? (
                            <Button
                              variant="outlined"
                              color="info"
                              onClick={() => handleClickScheduleSampling(item?.additionalParameters?.MedicalAnalysisBatchId)}
                              startIcon={<CalendarMonthIcon />}
                            >
                              {t('Portal_ProgramAnalysis')}
                            </Button>
                          ) : ['Consultation', 'Investigation'].includes(
                            item.additionalParameters?.Type as string
                          ) ? (
                            <Button
                              variant="outlined"
                              color="info"
                              onClick={() => handleOpenCalendar(item)}
                              startIcon={<CalendarMonthIcon />}
                            >
                              {t('Portal_Calendar')}
                            </Button>
                          ) : null}
                        </TableCell>
                      }
                      <TableCell sx={{ textAlign: 'right' }}>
                        {discount > 0 ? (
                          <>
                            <Typography variant="body1" sx={{ textDecoration: 'line-through', color: 'red' }}>
                              {item.service?.price.toFixed(2)}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'green' }}>
                              {discountedPrice.toFixed(2)}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body1" >{item.service?.price.toFixed(2)}</Typography>
                        )}
                      </TableCell>
                      {shoppingCartObject?.shoppingCartStatus?.id == 1 &&
                        <TableCell sx={{ textAlign: 'center', p: 0 }}>
                          <IconButton onClick={() => deleteItem(item)} disabled={!canEdit}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      }
                    </TableRow>
                  );
                })}
                {hasMedicalAnalysisBatch && (
                  <TableRow>
                    <TableCell>
                      <Box display="flex" alignItems="center" mb={2}>
                        <WarningAmberIcon color="warning" />
                        <Typography variant="body2" color="warning.main" ml={1}>
                          {t('Portal_ScheduleSamplingWarning')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell><Typography variant="h5">{t('Portal_Total')}</Typography></TableCell>
                  {(shoppingCartObject?.shoppingCartStatus?.id == 5) && <TableCell sx={{ textAlign: 'center' }}></TableCell>}
                  <TableCell sx={{ textAlign: 'right' }}>

                    <Typography variant="h5">{totalPrice.toFixed(2)}</Typography>
                  </TableCell>
                  {shoppingCartObject?.shoppingCartStatus?.id == 1 && <TableCell sx={{ textAlign: 'center', p: 0 }}></TableCell>}
                </TableRow>
              </Table>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ p: isMobile ? 0.5 : 3, boxShadow: 3, borderRadius: 3 }}>
            <CardContent>
              <Grid spacing={2} alignItems="center" container>
                {paymentTypes.map((p: any) => (
                  <Grid item xs={4}>
                    <Button
                      key={p.id}
                      onClick={() => setSelectedPaymentTypeId(p.id)}
                      disabled={!canEdit}
                      fullWidth
                      startIcon={!paymentTypeImages[p.id] && <PaymentIcon />}
                      sx={{
                        justifyContent: 'center', py: 1,
                        color: 'black',
                        backgroundColor: 'white',
                        border: selectedPaymentTypeId === p.id ? `2px solid ${theme.palette.success.dark}` : `2px solid ${theme.palette.info.dark}`,
                        '&:hover': {
                          backgroundColor: 'white',
                        },
                        '&.Mui-disabled': {
                          color: 'black',
                          backgroundColor: 'white',
                          border: selectedPaymentTypeId === p.id
                            ? `2px solid ${theme.palette.success.dark}`
                            : `2px solid ${theme.palette.info.dark}`,
                          opacity: 1,
                        },
                      }}
                    >
                      {
                        paymentTypeImages[p.id] ?
                          <img
                            src={paymentTypeImages[p.id] || '/assets/payments/default.png'}
                            alt={p.name}
                            style={{ height: 30 }}
                          /> :
                          <Typography variant="h5"
                            height={30}
                            display="flex"
                            alignItems="center"
                          >
                            {t('Portal_Type_' + p.tag)}
                          </Typography>
                      }

                    </Button>
                  </Grid>
                ))}
              </Grid>

              <TextField
                label={t("Portal_PromoCode")}
                value={promoCode}
                disabled={!canEdit}
                onChange={(e) => setPromoCode(e.target.value)}
                fullWidth
                sx={{ mt: 2 }}
              />
              <Typography variant="h6" sx={{ fontWeight: "bold", mt: 2 }}>
                {totalPrice !== items.reduce((sum, item) => sum + (item.service?.price || 0), 0) ? (
                  <>
                    <Typography variant="h6" sx={{ display: "inline", fontWeight: "bold" }}>
                      {t("Portal_Total")}:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ textDecoration: "line-through", color: "red", display: "inline", fontWeight: "bold", mx: 1 }}
                    >
                      {items.reduce((sum, item) => sum + (item.service?.price || 0), 0).toFixed(2)} lei
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "green", display: "inline" }}>
                      {totalPrice} lei
                    </Typography>
                  </>
                ) : (
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {t("Portal_Total")}: {totalPrice.toFixed(2)} lei
                  </Typography>
                )}
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    disabled={!canEdit}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    name="agree"
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('Portal_AgreementData') + ' '}
                    <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                      {t('Portal_Terms')}
                    </a>{" "}
                    /{" "}
                    <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                      {t('Portal_Policies')}
                    </a>.
                  </Typography>
                }
                sx={{ mt: 2 }}
              />

              {(shoppingCartObject?.shoppingCartStatus?.id === 2 || shoppingCartObject?.shoppingCartStatus?.id === 5 || shoppingCartObject?.shoppingCartStatus?.id === 6) && !loadingPayment ? (
                <Button variant="outlined" color="success" disabled={true}
                  fullWidth sx={{ mt: 2 }} >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                    <CheckCircleIcon color="success" sx={{ fontSize: '2rem', mr: 2 }} />
                    <Typography sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>{selectedPaymentTypeId != 1 ? t("Portal_Paid") + ' ' + (shoppingCartObject?.onlineTransaction?.paymentDate ? `(${format(shoppingCartObject?.onlineTransaction?.paymentDate, 'dd.MM.yyyy HH:mm')})` : '') : t("Portal_PaymentIsOk")}</Typography>
                  </Box>
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2, p: 2 }}
                  disabled={!agreeToTerms || !selectedPaymentTypeId || selectedPaymentTypeId == 0 || loadingPayment || paymentSuccess || !canEdit}
                  onClick={handlePayment}
                >
                  {loadingPayment ? (
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                      <CircularProgress color={'info'} size={'1rem'} sx={{ mr: 2 }} />
                      <Typography sx={{ color: theme.palette.text.primary }}>{t("Portal_TransactionInProcess")}...</Typography>
                    </Box>
                  ) : paymentSuccess ? (
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                      <CheckCircleIcon color="success" sx={{ fontSize: '2rem', mr: 2 }} />
                      <Typography sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>{selectedPaymentTypeId != 1 ? t("Portal_Paid") : t("Portal_PaymentIsOk")}</Typography>
                    </Box>
                  ) : (shoppingCartObject?.shoppingCartStatus?.id == 4 || shoppingCartObject?.shoppingCartStatus?.id == 7  || shoppingCartObject?.shoppingCartStatus?.id == 8)  ? (
                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                      <ErrorIcon color="error" sx={{ fontSize: '2rem', mr: 2 }} />
                      <Typography sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>
                         {shoppingCartObject?.shoppingCartStatus?.id == 4 ? t("Portal_PaymentError") :
                         shoppingCartObject?.shoppingCartStatus?.id == 7 ? t("Portal_PaymentCanceled") : 
                         shoppingCartObject?.shoppingCartStatus?.id == 8 ? t("Portal_Reimbursed") :
                         ''}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontWeight: "bold" }}>{t("Portal_PayOrder")}</Typography>
                  )
                  }
                </Button>
              )}
              {
                loadingPayment && selectedPaymentTypeId == 2 && paymentRef &&
                <Typography variant="body2">
                  {t("Portal_MissingQrCode")}
                  <a href="#" onClick={() =>
                    paymentWindowRef.current = window.open(
                      paymentRef,
                      'PaymentWindow',
                      'width=800,height=600,resizable=yes,scrollbars=yes,status=no,toolbar=no,menubar=no'
                    )
                  }>
                    {' ' + t('Portal_PressOpen')}
                  </a>
                </Typography>
              }
              {
                (shoppingCartObject?.shoppingCartStatus?.id == 2 || confirmed) &&
                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" mt={2} p={0}>
                  <CircularProgress color={'info'} size={'1rem'} sx={{ mr: 2 }} />
                  <Typography sx={{ color: theme.palette.text.primary }}>{t("Portal_CompletingTransaction")}</Typography>
                </Box>
              }
              {
                loadingPayment && !loading &&
                <Button variant="outlined" color="error" onClick={() => setCancelDialogOpen(true)}
                  fullWidth sx={{ mt: 2 }} >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                    <Typography sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>{t('Portal_CancelPayment')}</Typography>
                  </Box>
                </Button>
              }
              {
                !REACT_APP_APPLICATION_ISPROD && selectedPaymentTypeId && selectedPaymentTypeId == 2 && loadingPayment &&
                <Button variant="contained" color="success" onClick={simulatePayment}
                  fullWidth sx={{ mt: 2 }} >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                    <Typography sx={{ fontWeight: "bold", color: theme.palette.getContrastText(theme.palette.success.main) }}>Simulate MIA payment (DEV ONLY)</Typography>
                  </Box>
                </Button>
              }
              {
                !loadingPayment && !loading  &&
                <Button startIcon={<ShoppingCartIcon/>} variant="outlined" color="primary" onClick={() => window.open(AVANGARD_WEB_URL,'_self')}
                  fullWidth sx={{ mt: 2 }} >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                    <Typography sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>{t('Portal_AddNewCart')}</Typography>
                  </Box>
                </Button>
              }
              {
                canBeReimbursed  &&
                <Button variant="outlined" color="error" onClick={() => setReimbursmentDialogOpen(true)}
                  fullWidth sx={{ mt: 2 }} >
                  <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" p={0}>
                    <Typography sx={{ fontWeight: "bold", color: theme.palette.text.primary }}>{t('Portal_Reimbursment')}</Typography>
                  </Box>
                </Button>
              }
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <ConfirmationCommonDialog
        size='xs'
        title={t('Portal_DoYouAgree')}
        open={cancelDialogOpen}
        handleClose={() => setCancelDialogOpen(false)}
        text={
          <>
            <Typography variant='h5'>
              {t('Portal_DoYouWantToCancelThisCart')} <br /> {t('Portal_UndoneAction')}
            </Typography>
          </>
        }
        actions={[
          <Button
            onClick={() => setCancelDialogOpen(false)}
            color="success"
          >
            {t('Portal_No')}
          </Button>,
          <Button color='error' onClick={cancelPayment}>
            {t('Portal_AgreeToCancelCart')}
          </Button>,
        ]}
      />
      <ConfirmationCommonDialog
        size='xs'
        title={t('Portal_DoYouAgree')}
        open={pendingErrorOpen}
        handleClose={() => console.log('Make a choice')}
        text={
          <>
            <Typography variant='h5'>
              {t('Portal_DoYouWantToCancelPending')} {latestPendingShoppingCartObject?.onlineTransaction?.price?.toFixed(2)} lei? <br /> {t('Portal_UndoneAction')}
            </Typography>
          </>
        }
        actions={[
          <Button
            onClick={() => window.open('/shoppingcart/' + latestPendingShoppingCartObject?.shoppingCartIdentity, '_self')}
            color="success"
          >
            {t('Portal_No')}
          </Button>,
          <Button color='error' onClick={cancelPendingPayment}>
            {t('Portal_AgreeToCancelPreviousCart')}
          </Button>,
        ]}
      />
      <ConfirmationCommonDialog
        size='xs'
        title={t('Portal_DoYouAgree')}
        open={reimbursmentDialogOpen}
        handleClose={() => setReimbursmentDialogOpen(false)}
        text={
          <>
            <Typography variant='h5'>
              {t('Portal_DoYouWantToReimburse')}<br /> {t('Portal_UndoneAction')}
            </Typography>
          </>
        }
        actions={[
          <Button
            onClick={() => setReimbursmentDialogOpen(false)}
            color="success"
          >
            {t('Portal_No')}
          </Button>,
          <Button color='error' onClick={reimbursePayment}>
            {t('Portal_AgreeToReimburse')}
          </Button>,
        ]}
      />
      {
        shoppingCartIdentity &&
          <SamplingScheduleDialog
        isOpen={isOpenSchedule.state}
        onSave={reloadData}
        onClose={closeSchedule}
        shoppingCartIdentity={shoppingCartIdentity}
        medicalAnalysisBatchId={isOpenSchedule.medicalAnalysisBatchId}
      />
      }
    
    </>
  );
}

export default ShoppingCart;
